export interface Keymap {
  description: string;
  windows: string;
  mac: string;
  common?: string;
}

const arrowKeysMap: Record<string, string> = {
  // for reference: https://wincent.com/wiki/Unicode_representations_of_modifier_keys
  ARROWLEFT: '\u2190',
  ARROWRIGHT: '\u2192',
  ARROWUP: '\u2191',
  ARROWDOWN: '\u2193'
};

export const isApple = !!navigator.platform.match(/iphone|ipad|ipod|mac/i);

export function formatShortcut(keymap: Keymap): string | undefined {
  let shortcut: string;
  if (isApple) {
    // for reference: https://wincent.com/wiki/Unicode_representations_of_modifier_keys
    shortcut = keymap.mac
      .replace(/Cmd/i, '\u2318')
      .replace(/Shift/i, '\u21E7')
      .replace(/Ctrl/i, '\u2303')
      .replace(/Alt/i, '\u2325')
      .replace(/Backspace/i, '\u232B')
      .replace(/Enter/i, '\u23CE');
  } else {
    shortcut = keymap.windows.replace(/Backspace/i, '\u232B');
  }
  const keys = shortcut.split('-');
  let lastKey = keys[keys.length - 1];
  if (lastKey.length === 1) {
    // capitalise single letters
    lastKey = lastKey.toUpperCase();
  }
  keys[keys.length - 1] = arrowKeysMap[lastKey.toUpperCase()] || lastKey;
  return keys.join(isApple ? '' : '+');
}

export function tooltip(keymap?: Keymap, description?: string): string | undefined {
  if (keymap) {
    const shortcut = formatShortcut(keymap);
    return description ? `${description} ${shortcut}` : shortcut;
  }
  return;
}

function makeKeymap(description: string, windows: string, mac: string, common?: string) {
  return {
    description: description,
    windows: windows.replace(/Mod/i, 'Ctrl'),
    mac: mac.replace(/Mod/i, 'Cmd'),
    common: common
  };
}
export function makeKeyMapWithCommon(description: string, common: string) {
  const windows = common.replace(/Mod/i, 'Ctrl');
  const mac = common.replace(/Mod/i, 'Cmd');
  return makeKeymap(description, windows, mac, common);
}

export const addEntryBefore = makeKeyMapWithCommon('Add Entry Above', 'Ctrl-Alt-ArrowUp');
export const addEntryAfter = makeKeyMapWithCommon(
  'Add Entry Below',
  'Ctrl-Alt-ArrowDown'
);
export const backspace = makeKeyMapWithCommon('Backspace', 'Backspace');
export const deleteEntry = makeKeyMapWithCommon('Delete Entry', 'Ctrl-Backspace');
export const moveEntryDown = makeKeyMapWithCommon(
  'Move Entry Down',
  'Ctrl-Alt-Shift-ArrowDown'
);
export const moveEntryUp = makeKeyMapWithCommon(
  'Move Entry Up',
  'Ctrl-Alt-Shift-ArrowUp'
);
export const addFieldAfter = makeKeyMapWithCommon(
  'Add Field After',
  'Ctrl-Alt-ArrowRight'
);
export const addFieldBefore = makeKeyMapWithCommon(
  'Add Field Before',
  'Ctrl-Alt-ArrowLeft'
);
export const moveFieldLeft = makeKeyMapWithCommon(
  'Move Field Left',
  'Ctrl-Alt-Shift-ArrowLeft'
);
export const moveFieldRight = makeKeyMapWithCommon(
  'Move Field Right',
  'Ctrl-Alt-Shift-ArrowRight'
);
export const openComments = makeKeyMapWithCommon('Open Comments', 'Cmd-Alt-C');
